import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { FileVideo, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { formatErr } from "../lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isSafeInternalPath(value) {
  // Accept application-relative paths only. This keeps the durable returnTo
  // query useful after a refresh without allowing an external redirect.
  return typeof value === "string" && value.startsWith("/") && !value.startsWith("//");
}

function dashboardFor(user) {
  if (process.env.REACT_APP_NATIVE_SHELL === "society" && ["client", "society_staff", "society_member"].includes(user?.role)) {
    if (user?.role === "society_staff" && user?.societyRole === "security") return "/society/guard";
    return "/society/mobile";
  }
  if (user?.role === "admin") return "/admin";
  if (user?.role === "dealer") return "/dealer";
  if (user?.role === "auditor") return "/society/audit";
  if (user?.role === "society_staff") return user?.societyRole === "security" ? "/society/guard" : "/society/panel";
  if (user?.role === "society_member") return "/society/mobile";
  return "/client";
}

function validateIdentifier(value) {
  const trimmed = value.trim();
  if (!trimmed) return "Mobile number or email is required.";
  if (trimmed.includes("@")) return EMAIL_RE.test(trimmed.toLowerCase()) ? "" : "Enter a valid email address.";
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length < 10 || digits.length > 15) return "Enter a valid mobile number.";
  return "";
}

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const queryReturnTo = new URLSearchParams(location.search).get("returnTo") || "";
  // The query parameter is the refresh-safe source. Retain state as a
  // compatibility fallback for links created by older deployed builds.
  const requestedPath = isSafeInternalPath(queryReturnTo)
    ? queryReturnTo
    : isSafeInternalPath(location.state?.returnTo)
      ? location.state.returnTo
      : "";
  const isSocietyShell = process.env.REACT_APP_NATIVE_SHELL === "society";
  const [societyCode, setSocietyCode] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [fieldErr, setFieldErr] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Ravi: token-expiry fix -- when api.js can't silently refresh an expired
  // session, it clears storage and hard-redirects here. Surface a clear
  // reason instead of just dropping the guard/resident back at a blank
  // login form with no explanation.
  useEffect(() => {
    try {
      if (sessionStorage.getItem("auth_session_expired")) {
        sessionStorage.removeItem("auth_session_expired");
        toast.info("Your session expired. Please sign in again.");
      }
    } catch {}
  }, []);

  if (user && user !== false) {
    return <Navigate to={requestedPath || dashboardFor(user)} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    const nextErrors = {
      societyCode: isSocietyShell && !societyCode.trim() ? "Society code is required." : "",
      identifier: validateIdentifier(identifier),
      password: password ? "" : "Password is required.",
    };
    setFieldErr(nextErrors);
    setErr("");
    if (nextErrors.societyCode || nextErrors.identifier || nextErrors.password) return;

    try {
      setBusy(true);
      const signedInUser = await login(identifier.trim(), password, rememberMe, isSocietyShell ? { societyCode: societyCode.trim() } : {});
      toast.success(`Welcome, ${signedInUser.name}`);
      nav(requestedPath || dashboardFor(signedInUser), { replace: true });
    } catch (error) {
      setErr(formatErr(error.response?.data?.detail || error) || error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-white lg:flex">
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-[#111827] p-12 text-white lg:flex">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="relative">
          <div className="mb-16 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-sm bg-white text-[#111827]">
              <FileVideo className="h-5 w-5" />
            </div>
            <div className="font-display text-xl font-extrabold tracking-tight">RP SIGNAGE</div>
          </div>
          <div className="mb-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-white/60">
            B2B / Control Panel
          </div>
          <h1 className="font-display text-5xl font-extrabold leading-[0.95] tracking-tighter xl:text-6xl">
            One pane <br /> of glass for <br /> every screen.
          </h1>
          <p className="mt-6 max-w-md text-sm text-white/70">
            Manage dealers, clients, signage templates and wallets across Cloud, USB and Hybrid plans.
          </p>
        </div>
        <div className="relative grid grid-cols-3 gap-3 text-[11px] font-semibold uppercase tracking-[0.2em]">
          <div className="rounded-sm border border-white/20 px-3 py-3">
            <div className="mb-2 text-[10px] text-white/50">Plan 01</div>
            <div>Cloud</div>
          </div>
          <div className="rounded-sm border border-white/20 px-3 py-3">
            <div className="mb-2 text-[10px] text-white/50">Plan 02</div>
            <div>USB</div>
          </div>
          <div className="rounded-sm border border-white/20 px-3 py-3">
            <div className="mb-2 text-[10px] text-white/50">Plan 03</div>
            <div>Hybrid</div>
          </div>
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center px-4 py-8 sm:px-6 lg:p-8">
        <form onSubmit={submit} className="w-full max-w-md lg:max-w-sm" data-testid="login-form">
          <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#6B7280]">Sign in</div>
          <h2 className="font-display mb-2 text-3xl font-extrabold tracking-tighter sm:text-4xl">Access the panel.</h2>
          <p className="mb-8 text-sm text-[#6B7280]">Enter your credentials to continue.</p>

          {err && (
            <div data-testid="login-error" className="mb-4 rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {err}
            </div>
          )}

          <div className="space-y-4">
            {isSocietyShell && (
              <div>
                <Label htmlFor="societyCode" className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">
                  Society Code
                </Label>
                <Input
                  id="societyCode"
                  value={societyCode}
                  onChange={(e) => setSocietyCode(e.target.value.toUpperCase())}
                  className="mt-1.5 rounded-sm border-[#E5E7EB] focus-visible:ring-[#111827]"
                  placeholder="Example: RP-C101"
                  autoComplete="organization"
                  data-testid="login-society-code"
                  required
                />
                {fieldErr.societyCode && <div className="mt-1 text-xs font-semibold text-red-600">{fieldErr.societyCode}</div>}
              </div>
            )}

            <div>
              <Label htmlFor="identifier" className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">
                Mobile Number or Email
              </Label>
              <Input
                id="identifier"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="mt-1.5 rounded-sm border-[#E5E7EB] focus-visible:ring-[#111827]"
                placeholder="Mobile Number or Email"
                autoComplete="username"
                data-testid="login-identifier"
                required
              />
              {fieldErr.identifier && <div className="mt-1 text-xs font-semibold text-red-600">{fieldErr.identifier}</div>}
            </div>

            <div>
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 rounded-sm border-[#E5E7EB] focus-visible:ring-[#111827]"
                placeholder="Password"
                autoComplete="current-password"
                data-testid="login-password"
                required
              />
              {fieldErr.password && <div className="mt-1 text-xs font-semibold text-red-600">{fieldErr.password}</div>}
            </div>

            <div className="flex items-center justify-between gap-3">
              <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-[#374151]">
                <Checkbox checked={rememberMe} onCheckedChange={(checked) => setRememberMe(Boolean(checked))} />
                Remember Me
              </label>
              <button type="button" onClick={() => setErr("Please contact your dealer or administrator to reset your password.")} className="text-sm font-bold text-blue-700">
                Forgot Password?
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={busy}
            className="mt-6 w-full rounded-sm bg-[#111827] text-white hover:bg-[#374151]"
            data-testid="login-submit"
          >
            {busy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...
              </>
            ) : (
              "Login"
            )}
          </Button>

          <div className="mt-6 border-t border-[#E5E7EB] pt-6 text-sm text-[#6B7280]">
            Don&apos;t have an account? <span className="font-bold text-[#111827]">Contact your Dealer</span>
          </div>
        </form>
      </div>
    </div>
  );
}
